import { collection, doc, getDocs, onSnapshot, setDoc, deleteDoc } from 'firebase/firestore'
import type { Bill } from '../../types'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'

const BILLS_UPDATED_EVENT = 'wortelogy-bills-updated'

const billsCacheByPlace: Record<string, Bill[]> = {}
let billsUnsubscribe: (() => void) | null = null

// Remove all undefined values recursively so Firestore setDoc never receives them.
function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) =>
      v && typeof v === 'object' ? stripUndefined(v as any) : v,
    ) as unknown as T
  }
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue
      if (Array.isArray(v) || (v && typeof v === 'object')) {
        result[key] = stripUndefined(v as any)
      } else {
        result[key] = v
      }
    }
    return result as T
  }
  return value
}

function billsRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'bills')
}

function billDoc(placeId: string, billId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'bills', billId)
}

export async function primeBillsCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (billsUnsubscribe) {
    billsUnsubscribe()
    billsUnsubscribe = null
  }
  const snap = await getDocs(billsRef(placeId))
  billsCacheByPlace[placeId] = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Bill))
  billsUnsubscribe = onSnapshot(billsRef(placeId), (snapshot) => {
    billsCacheByPlace[placeId] = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Bill))
    window.dispatchEvent(new CustomEvent(BILLS_UPDATED_EVENT))
  })
}

export function getBills(): Bill[] {
  const placeId = getCurrentPlaceId()
  return billsCacheByPlace[placeId] ?? []
}

export function getBill(id: string): Bill | undefined {
  const placeId = getCurrentPlaceId()
  const cache = billsCacheByPlace[placeId] ?? []
  return cache.find((b) => b.id === id)
}

export function saveBills(bills: Bill[]): void {
  const placeId = getCurrentPlaceId()
  billsCacheByPlace[placeId] = bills
  window.dispatchEvent(new CustomEvent(BILLS_UPDATED_EVENT))
  ;(async () => {
    const snap = await getDocs(billsRef(placeId))
    const ids = new Set(bills.map((b) => b.id))
    for (const d of snap.docs) {
      if (!ids.has(d.id)) await deleteDoc(d.ref)
    }
    for (const bill of bills) {
      const sanitized = stripUndefined(bill)
      await setDoc(billDoc(placeId, bill.id), sanitized)
    }
  })().catch((e) => console.error('Firestore saveBills failed', e))
}

function generateBillId(): string {
  const now = new Date()
  const date = now.toISOString().slice(0, 10).replace(/-/g, '')
  const time = now.toTimeString().slice(0, 8).replace(/:/g, '')
  return `BILL-${date}-${time}`
}

export function addBill(bill: Omit<Bill, 'id'>): Bill {
  const placeId = getCurrentPlaceId()
  const id = generateBillId()
  const full: Bill = stripUndefined({ ...bill, id })
  const cache = billsCacheByPlace[placeId] ?? []
  billsCacheByPlace[placeId] = [...cache, full]
  setDoc(billDoc(placeId, id), full).catch((e) =>
    console.error('Firestore addBill failed', e),
  )
  window.dispatchEvent(new CustomEvent(BILLS_UPDATED_EVENT))
  return full
}

export function updateBill(id: string, patch: Partial<Bill>): Bill[] {
  const placeId = getCurrentPlaceId()
  const cache = billsCacheByPlace[placeId] ?? []
  billsCacheByPlace[placeId] = cache.map((b) => (b.id === id ? { ...b, ...patch } : b))
  const found = billsCacheByPlace[placeId].find((b) => b.id === id)
  if (found) {
    const sanitized = stripUndefined(found)
    setDoc(billDoc(placeId, id), sanitized).catch((e) =>
      console.error('Firestore updateBill failed', e),
    )
  }
  window.dispatchEvent(new CustomEvent(BILLS_UPDATED_EVENT))
  return billsCacheByPlace[placeId]
}

export function isBillsPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in billsCacheByPlace
}

/** Clear in-memory cache and unsubscribe from real-time listener. */
export function clearBillsCache(): void {
  if (billsUnsubscribe) {
    billsUnsubscribe()
    billsUnsubscribe = null
  }
  Object.keys(billsCacheByPlace).forEach((k) => delete billsCacheByPlace[k])
}
